import { useMemo, useState } from 'react';
import { Calendar, ChevronDown, ChevronUp, Building2, MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { HOSPITAL_COLORS } from './CarePlanMap';
import type { CarePlanScenario, CarePlanAssignment } from '@/hooks/useCarePlan';

interface CareCalendarProps {
  scenarios: CarePlanScenario[];
  assignments: CarePlanAssignment[];
  onHospitalSelect: (id: string | null) => void;
  selectedHospitalId: string | null;
}

function formatUsd(amount: number | null): string {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
}

interface DayGroup {
  dayNumber: number;
  dayLabel: string;
  phase: string;
  scenarioName: string;
  scenarioId: string;
  hospitalId: string | null;
  hospitalName: string | null;
  hospitalCity: string | null;
  hospitalState: string | null;
  hospitalColorIndex: number;
  distance: number | null;
  totalCost: number;
  procedures: CarePlanAssignment[];
  isAnchored: boolean;
}

function dayLabel(day: number): string {
  if (day < 0) return `${Math.abs(day)} days before`;
  if (day === 0) return 'Day 0';
  if (day === 1) return 'Day 1';
  if (day <= 7) return `Day ${day}`;
  if (day <= 30) return `Week ${Math.ceil(day / 7)}`;
  if (day <= 365) return `Month ${Math.ceil(day / 30)}`;
  return `Year ${Math.ceil(day / 365)}`;
}

export function CareCalendar({ scenarios, assignments, onHospitalSelect, selectedHospitalId }: CareCalendarProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const [expandedDay, setExpandedDay] = useState<string | null>(null);

  // Build hospital color map
  const hospitalColorMap = useMemo(() => {
    const map = new Map<string, number>();
    let idx = 0;
    for (const a of assignments) {
      if (a.assigned_hospital_id && !a.is_blended && !map.has(a.assigned_hospital_id)) {
        map.set(a.assigned_hospital_id, idx++);
      }
    }
    return map;
  }, [assignments]);

  // Group assignments by (scenario, day_number) into visual day groups
  const dayGroups = useMemo(() => {
    const groups: DayGroup[] = [];
    const scenarioMap = new Map(scenarios.map(s => [s.id, s]));

    // Group by care_plan_scenario_id + day_number
    const groupKey = (a: CarePlanAssignment) => `${a.care_plan_scenario_id}:${a.day_number ?? 'none'}`;
    const grouped = new Map<string, CarePlanAssignment[]>();
    for (const a of assignments) {
      const key = groupKey(a);
      const list = grouped.get(key) || [];
      list.push(a);
      grouped.set(key, list);
    }

    for (const [key, procs] of grouped.entries()) {
      const first = procs[0];
      const scenario = scenarioMap.get(first.care_plan_scenario_id);
      const day = first.day_number ?? 0;

      // Determine primary hospital for this day group
      const hospCount = new Map<string, number>();
      for (const p of procs) {
        if (p.assigned_hospital_id && !p.is_blended) {
          hospCount.set(p.assigned_hospital_id, (hospCount.get(p.assigned_hospital_id) || 0) + 1);
        }
      }
      let primaryHospId: string | null = null;
      let maxCount = 0;
      for (const [hid, count] of hospCount.entries()) {
        if (count > maxCount) { maxCount = count; primaryHospId = hid; }
      }

      const primaryProc = procs.find(p => p.assigned_hospital_id === primaryHospId) || first;
      const isAnchored = procs.some(p => p.assignment_reason === 'anchored_day');
      const totalCost = procs.reduce((s, p) => s + (p.hospital_total_price || 0), 0);

      groups.push({
        dayNumber: day,
        dayLabel: dayLabel(day),
        phase: first.phase || 'Procedures',
        scenarioName: scenario?.scenario_name || 'Scenario',
        scenarioId: first.care_plan_scenario_id,
        hospitalId: primaryHospId,
        hospitalName: primaryProc.assigned_hospital_name,
        hospitalCity: primaryProc.assigned_hospital_city,
        hospitalState: primaryProc.assigned_hospital_state,
        hospitalColorIndex: primaryHospId ? (hospitalColorMap.get(primaryHospId) ?? 0) : -1,
        distance: primaryProc.distance_miles,
        totalCost,
        procedures: procs,
        isAnchored,
      });
    }

    // Sort by scenario sort order, then day_number
    const scenarioOrder = new Map(scenarios.map((s, i) => [s.id, i]));
    groups.sort((a, b) => {
      const sa = scenarioOrder.get(a.scenarioId) ?? 0;
      const sb = scenarioOrder.get(b.scenarioId) ?? 0;
      if (sa !== sb) return sa - sb;
      return a.dayNumber - b.dayNumber;
    });

    return groups;
  }, [scenarios, assignments, hospitalColorMap]);

  // Group by scenario for section headers
  const scenarioGroups = useMemo(() => {
    const result: { scenarioId: string; scenarioName: string; days: DayGroup[] }[] = [];
    let current: typeof result[0] | null = null;
    for (const dg of dayGroups) {
      if (!current || current.scenarioId !== dg.scenarioId) {
        current = { scenarioId: dg.scenarioId, scenarioName: dg.scenarioName, days: [] };
        result.push(current);
      }
      current.days.push(dg);
    }
    return result;
  }, [dayGroups]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Calendar className={`w-4.5 h-4.5 ${isAhp ? (ahpDark ? 'text-civic-navy-soft' : 'text-civic-navy') : 'text-matrix-green'}`} />
        <h2 className={`text-lg font-semibold ${isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'}`}>
          Care Calendar
        </h2>
      </div>

      {scenarioGroups.map(sg => (
        <div key={sg.scenarioId}>
          {/* Scenario label */}
          <div className={`text-xs font-semibold uppercase tracking-wider mb-2 px-1 ${
            isAhp ? (ahpDark ? 'text-civic-navy-soft' : 'text-civic-navy') : 'text-matrix-green'
          }`}>
            {sg.scenarioName}
          </div>

          {/* Day cards */}
          <div className="space-y-1.5">
            {sg.days.map((dg, i) => {
              const dayKey = `${dg.scenarioId}:${dg.dayNumber}`;
              const isExpanded = expandedDay === dayKey;
              const color = dg.hospitalColorIndex >= 0
                ? HOSPITAL_COLORS[dg.hospitalColorIndex % HOSPITAL_COLORS.length]
                : (isAhp ? '#9ca3af' : '#6b7280');
              const isSelected = dg.hospitalId === selectedHospitalId;

              return (
                <div key={dayKey}>
                  <button
                    onClick={() => {
                      setExpandedDay(isExpanded ? null : dayKey);
                      if (dg.hospitalId) onHospitalSelect(isExpanded ? null : dg.hospitalId);
                    }}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                      isSelected
                        ? isAhp
                          ? ahpDark ? 'border-civic-navy-soft/50 bg-civic-navy-soft/10 shadow-sm' : 'border-civic-navy bg-blue-50/60 shadow-sm'
                          : 'border-matrix-green/50 bg-matrix-green/5'
                        : isAhp
                          ? ahpDark ? 'border-civic-gold/15 bg-civic-card-dark hover:border-civic-gold/30' : 'border-gray-200 bg-white hover:border-gray-300'
                          : 'border-foreground/10 bg-card hover:border-foreground/20'
                    }`}
                  >
                    {/* Day timeline dot + line */}
                    <div className="flex flex-col items-center shrink-0 w-10">
                      <div
                        className="w-3 h-3 rounded-full border-2 border-white shadow-sm"
                        style={{ backgroundColor: color }}
                      />
                      {i < sg.days.length - 1 && (
                        <div className={`w-px flex-1 mt-1 min-h-[8px] ${
                          isAhp ? (ahpDark ? 'bg-civic-gold/15' : 'bg-gray-200') : 'bg-foreground/10'
                        }`} />
                      )}
                    </div>

                    {/* Day info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold ${isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'}`}>
                          {dg.dayLabel}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          isAhp ? (ahpDark ? 'bg-white/10 text-gray-400' : 'bg-gray-100 text-gray-500') : 'bg-foreground/10 text-muted-foreground'
                        }`}>
                          {dg.phase}
                        </span>
                        {dg.isAnchored && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            isAhp ? (ahpDark ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-50 text-blue-600') : 'bg-blue-500/10 text-blue-400'
                          }`}>
                            same facility
                          </span>
                        )}
                      </div>

                      {/* Hospital name */}
                      {dg.hospitalName && (
                        <div className={`text-xs mt-0.5 flex items-center gap-1 ${
                          isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-500') : 'text-muted-foreground'
                        }`}>
                          <Building2 className="w-3 h-3 shrink-0" />
                          <span className="truncate">{dg.hospitalName}</span>
                          {dg.distance !== null && (
                            <span className="shrink-0">· {dg.distance.toFixed(0)}mi</span>
                          )}
                        </div>
                      )}

                      {/* Procedure summary */}
                      <p className={`text-[11px] mt-0.5 ${isAhp ? (ahpDark ? 'text-gray-500' : 'text-gray-400') : 'text-muted-foreground/70'}`}>
                        {dg.procedures.length} procedure{dg.procedures.length !== 1 ? 's' : ''}
                        {dg.procedures.length <= 3 && (
                          <> — {dg.procedures.map(p => p.description || p.code).join(', ')}</>
                        )}
                      </p>
                    </div>

                    {/* Cost + chevron */}
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-sm font-bold ${isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'}`}>
                        {formatUsd(dg.totalCost)}
                      </span>
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </div>
                  </button>

                  {/* Expanded procedure detail */}
                  {isExpanded && (
                    <div className={`ml-[52px] mt-1 mb-2 rounded-lg border overflow-hidden ${
                      isAhp ? (ahpDark ? 'border-civic-gold/10 bg-civic-bg-dark' : 'border-gray-100 bg-gray-50') : 'border-foreground/5 bg-foreground/[0.02]'
                    }`}>
                      {dg.procedures.map(p => {
                        const pColor = p.assigned_hospital_id
                          ? HOSPITAL_COLORS[(hospitalColorMap.get(p.assigned_hospital_id) ?? 0) % HOSPITAL_COLORS.length]
                          : undefined;
                        const splitFromAnchor = p.assigned_hospital_id !== dg.hospitalId && !p.is_blended;

                        return (
                          <div
                            key={p.id}
                            className={`flex items-center gap-2 px-3 py-2 border-b last:border-b-0 ${
                              isAhp ? (ahpDark ? 'border-civic-gold/8' : 'border-gray-100') : 'border-foreground/5'
                            }`}
                          >
                            {pColor && (
                              <div
                                className="w-1.5 h-1.5 rounded-full shrink-0"
                                style={{ backgroundColor: pColor }}
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs truncate ${isAhp ? (ahpDark ? 'text-gray-300' : 'text-gray-700') : 'text-foreground/80'}`}>
                                {p.description || p.code}
                              </p>
                              <p className={`text-[10px] ${isAhp ? (ahpDark ? 'text-gray-500' : 'text-gray-400') : 'text-muted-foreground'}`}>
                                {p.code_type}:{p.code}
                                {p.quantity > 1 ? ` x${p.quantity}` : ''}
                                {splitFromAnchor && p.assigned_hospital_name && (
                                  <span className="italic"> — at {p.assigned_hospital_name}</span>
                                )}
                                {(p.assignment_reason === 'consolidated' || p.assignment_reason === 'consolidated_day') && (
                                  <span className="italic"> — grouped for convenience</span>
                                )}
                                {p.is_blended && <span className="italic"> — national avg</span>}
                              </p>
                            </div>
                            <span className={`text-xs font-medium shrink-0 ${isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'}`}>
                              {formatUsd(p.hospital_total_price)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
